/**
 * Subscription purchase option (PDP + quick-add drawer).
 * Custom element so it re-initializes when quick-add injects HTML via innerHTML.
 *
 * Spar-Abo free gift (metafield PDP: Abo Gratisprodukt):
 * When the customer adds to cart with Spar-Abo selected, also adds the gift
 * variant (1×) with line properties so a Shopify automatic discount / discount
 * app can zero it later. Gift line properties: _free_abo, _abo_gift.
 * Incentive is once per gift product (Warengruppe proxy): if the same gift
 * variant is already in cart as an abo gift, it is not added again.
 *
 * Link between Spar-Abo parent and gift (Warengruppe):
 * - Parent line gets properties[_abo_gift_variant_id] = gift variant id
 * - Gift line gets _abo_gift_variant_id (same) plus _free_abo / _abo_gift
 * Cart remove reconciles: gift is removed only when no parent with that
 * _abo_gift_variant_id remains.
 * Until that discount exists, the gift is added at its normal price.
 */
import { ThemeEvents, CartUpdateEvent, CartGroupedSections } from "@theme/events";
import { fetchConfig } from "@theme/utilities";

class WeSiteAbonnement extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {boolean} */
  #addingGift = false;

  connectedCallback() {
    if (this.dataset.bound === "true") return;
    this.dataset.bound = "true";
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const ui = this.querySelector("[data-we-site-abonnement]");
    const plansEl = this.querySelector("[data-we-site-abonnement-plans]");
    const modeInputs = this.querySelectorAll("[data-we-site-abonnement-mode]");
    const planSelect = this.querySelector("[data-we-site-abonnement-plan]");
    const consentSlot = this.querySelector("[data-we-site-abonnement-consent]");
    const onetimePriceEl = this.querySelector("[data-we-site-onetime-price]");
    const aboPriceEl = this.querySelector("[data-we-site-abo-price]");
    const aboCompareEl = this.querySelector("[data-we-site-abo-compare]");
    const optionCards = this.querySelectorAll(".we-site-pdp-abonnement__option");
    const productId = this.dataset.productId;

    let plansByVariant = [];
    try {
      plansByVariant = JSON.parse(plansEl?.textContent || "[]");
    } catch (e) {
      plansByVariant = [];
    }

    const moneyFormat = ui?.dataset.moneyFormat || "";

    const formatMoney = (cents) => {
      const amount = (Number(cents) / 100).toFixed(2).replace(".", ",");
      const amountNoDecimals = String(Math.round(Number(cents) / 100));
      if (!moneyFormat) return `${amount} €`;
      return moneyFormat
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, amount)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, amountNoDecimals)
        .replace(/\{\{\s*amount\s*\}\}/g, amount.replace(",", "."));
    };

    const getScope = () =>
      this.closest(".quick-add__content") ||
      this.closest(".product-information") ||
      this.closest("dialog") ||
      document;

    const getProductForm = () => {
      const scope = getScope();
      return (
        scope.querySelector(
          `product-form-component[data-product-id="${productId}"] form[data-type="add-to-cart-form"]`
        ) ||
        document.querySelector(
          `product-form-component[data-product-id="${productId}"] form[data-type="add-to-cart-form"]`
        )
      );
    };

    const getSellingPlanInput = () => {
      const form = getProductForm();
      return form?.querySelector('input.selected-selling-plan-id, input[name="selling_plan"]') || null;
    };

    /**
     * Variant id from the checked Größe/Menge radio (updated before variant:selected).
     * Prefer this over the add-to-cart form input, which product-form only updates on
     * variant:update — often after abonnement has already refreshed prices.
     * @returns {number | null}
     */
    const getPickerVariantId = () => {
      const scope = getScope();
      const checked =
        scope.querySelector(
          `variant-picker[data-product-id="${productId}"] input[type="radio"][data-variant-id]:checked`
        ) ||
        scope.querySelector("variant-picker input[type='radio'][data-variant-id]:checked");
      if (!(checked instanceof HTMLInputElement) || !checked.dataset.variantId) return null;
      const id = Number(checked.dataset.variantId);
      return Number.isFinite(id) ? id : null;
    };

    const getFormVariantId = () => {
      const form = getProductForm();
      const input = form?.querySelector('input[name="id"]');
      if (!input?.value) return null;
      const id = Number(input.value);
      return Number.isFinite(id) ? id : null;
    };

    /**
     * @param {Event | null} [event]
     * @returns {number | null}
     */
    const resolveVariantId = (event = null) => {
      const detail = /** @type {CustomEvent | null} */ (event)?.detail;
      const fromEvent = detail?.resource?.id ?? detail?.variant?.id;
      if (fromEvent != null && fromEvent !== "") {
        const id = Number(fromEvent);
        if (Number.isFinite(id)) return id;
      }

      return getPickerVariantId() ?? getFormVariantId();
    };

    /** @type {number | null} */
    let currentVariantId = resolveVariantId();

    const getVariantData = (variantId) => {
      if (variantId == null || !Number.isFinite(Number(variantId))) return null;
      return plansByVariant.find((v) => Number(v.id) === Number(variantId)) || null;
    };

    const moveBuyerConsent = () => {
      if (!consentSlot) return;
      const consent = document.getElementById("shopify-buyer-consent");
      if (consent && consent.parentElement !== consentSlot) {
        consentSlot.appendChild(consent);
      }
    };

    const ABO_GIFT_VARIANT_PROP = "properties[_abo_gift_variant_id]";

    /**
     * Ensures Spar-Abo parent lines carry the gift variant id as a hidden
     * property so cart remove can drop orphaned Warengruppe gifts.
     */
    const syncAboGiftLinkProperty = () => {
      const form = getProductForm();
      if (!form) return;

      let input = form.querySelector(`input[name="${ABO_GIFT_VARIANT_PROP}"]`);
      const mode = this.querySelector("[data-we-site-abonnement-mode]:checked")?.value || "onetime";
      const giftVariantId = this.dataset.giftVariantId;

      if (mode === "abonnement" && giftVariantId) {
        if (!(input instanceof HTMLInputElement)) {
          input = document.createElement("input");
          input.type = "hidden";
          input.name = ABO_GIFT_VARIANT_PROP;
          form.appendChild(input);
        }
        input.value = String(giftVariantId);
        return;
      }

      input?.remove();
    };

    const setModeUI = (mode) => {
      optionCards.forEach((card) => {
        const input = card.querySelector("[data-we-site-abonnement-mode]");
        const selected = input?.value === mode && input?.checked;
        card.classList.toggle("is-selected", Boolean(selected));
      });
      if (mode === "abonnement") moveBuyerConsent();
    };

    const syncSellingPlan = () => {
      const input = getSellingPlanInput();
      const mode = this.querySelector("[data-we-site-abonnement-mode]:checked")?.value || "onetime";
      if (input) {
        if (mode === "abonnement" && planSelect && !planSelect.disabled && planSelect.value) {
          input.value = planSelect.value;
        } else {
          input.value = "";
        }
      }
      syncAboGiftLinkProperty();
      document.dispatchEvent(
        new CustomEvent("selling-plan:changed", {
          bubbles: true,
          detail: {
            sellingPlanId: input?.value || null,
            sellingPlanPrice: Number(planSelect?.selectedOptions?.[0]?.dataset?.price || 0) || null,
          },
        })
      );
    };

    /**
     * Selected Packung radio (VE), if present for this product.
     * @returns {HTMLInputElement | null}
     */
    const getSelectedPackung = () => {
      const scope = getScope();
      const productSelector = productId
        ? `[data-we-site-packung][data-product-id="${productId}"] input[type="radio"]:checked`
        : null;
      const radio =
        (productSelector && scope.querySelector(productSelector)) ||
        scope.querySelector("[data-we-site-packung]:not([hidden]) input[type='radio']:checked") ||
        scope.querySelector("[data-we-site-packung] input[type='radio']:checked");
      return radio instanceof HTMLInputElement ? radio : null;
    };

    const getFormComponent = () => getProductForm()?.closest("product-form-component") || null;

    /**
     * Pack / line multiplier shown next to ATC (VE pack-count, else quantity).
     * @returns {number}
     */
    const getLineCount = () => {
      const formComp = getFormComponent();
      if (!formComp) return 1;

      if (formComp.dataset.veEnabled === "true") {
        const packControl = formComp.querySelector("[data-ve-pack-count]");
        if (packControl instanceof HTMLInputElement || packControl instanceof HTMLSelectElement) {
          const packCount = parseInt(packControl.value, 10);
          return Number.isFinite(packCount) && packCount > 0 ? packCount : 1;
        }
      }

      const qtyControl = formComp.querySelector(
        'quantity-selector-component input[ref="quantityInput"], select[name="quantity"], input[name="quantity"]:not([data-ve-cart-quantity])'
      );
      if (qtyControl instanceof HTMLInputElement || qtyControl instanceof HTMLSelectElement) {
        const qty = parseInt(qtyControl.value, 10);
        return Number.isFinite(qty) && qty > 0 ? qty : 1;
      }
      return 1;
    };

    /**
     * Pack total for current unit price (floor discount per unit, then × veSize).
     * Matches ATC / cart line math in we-site-ve-quantity + product-form.
     * @param {number} unitCents
     * @returns {number}
     */
    const packTotalFromUnit = (unitCents) => {
      const pack = getSelectedPackung();
      if (!pack) return Number(unitCents) || 0;
      const veSize = Number(pack.dataset.veSize) || 1;
      const discountPct = Number(pack.dataset.discountPct) || 0;
      const size = veSize > 0 ? veSize : 1;
      const unit = Number(unitCents) || 0;
      const discountedUnit = discountPct > 0 ? unit - Math.floor((unit * discountPct) / 100) : unit;
      return discountedUnit * size;
    };

    /**
     * Updates Einmalige / Spar-Abo prices for the current variant + Packung + Menge.
     * Same total as the ATC Endpreis (pack unit × line count).
     * @param {HTMLOptionElement | null} [option]
     */
    const updateDisplayedPrices = (option = null) => {
      const variantData =
        getVariantData(currentVariantId) || getVariantData(resolveVariantId());
      if (!variantData) return;

      currentVariantId = Number(variantData.id);
      const unitOnetime = Number(variantData.price) || 0;
      const lineCount = getLineCount();
      const onetimeTotal = packTotalFromUnit(unitOnetime) * lineCount;

      if (onetimePriceEl && onetimeTotal >= 0) {
        onetimePriceEl.textContent = formatMoney(onetimeTotal);
      }

      if (!aboPriceEl) return;

      const planOption = option || planSelect?.selectedOptions?.[0] || null;
      const planUnit = Number(planOption?.dataset?.price);
      const planCompareUnit = Number(planOption?.dataset?.compare);
      const aboUnit = Number.isFinite(planUnit) && planUnit > 0 ? planUnit : unitOnetime;
      const aboTotal = packTotalFromUnit(aboUnit) * lineCount;
      const compareUnit =
        Number.isFinite(planCompareUnit) && planCompareUnit > 0 ? planCompareUnit : unitOnetime;
      const compareTotal = packTotalFromUnit(compareUnit) * lineCount;

      if (!Number.isNaN(aboTotal)) aboPriceEl.textContent = formatMoney(aboTotal);
      if (aboCompareEl) {
        if (compareTotal > aboTotal) {
          aboCompareEl.textContent = formatMoney(compareTotal);
          aboCompareEl.classList.remove("is-hidden");
        } else {
          aboCompareEl.classList.add("is-hidden");
        }
      }
    };

    const updatePricesFromOption = (option) => {
      updateDisplayedPrices(option);
    };

    const rebuildPlanSelect = (variantData) => {
      if (planSelect) {
        const plans = variantData?.plans || [];
        planSelect.innerHTML = "";

        if (!plans.length) {
          planSelect.disabled = true;
          ["Lieferung alle 14 Tage", "Lieferung alle 28 Tage", "Lieferung alle 42 Tage"].forEach((label, i) => {
            const opt = document.createElement("option");
            opt.value = "";
            opt.textContent = label;
            if (i === 0) opt.selected = true;
            planSelect.appendChild(opt);
          });
        } else {
          planSelect.disabled = false;
          plans.forEach((plan, index) => {
            const opt = document.createElement("option");
            opt.value = String(plan.id);
            opt.textContent = plan.title_override || plan.option || plan.name;
            opt.dataset.price = String(plan.price);
            opt.dataset.compare = String(plan.compare_at_price || plan.price);
            if (index === 0) opt.selected = true;
            planSelect.appendChild(opt);
          });
        }
      }

      // Always refresh Einmalige (+ Abo) — including products without Spar-Abo / planSelect.
      updateDisplayedPrices(planSelect?.selectedOptions?.[0] || null);
    };

    /**
     * @param {Event | null} [event]
     */
    const onVariantChange = (event = null) => {
      // variant:selected only carries optionValueId — ignore its detail.id
      const eventForId =
        event &&
        (event.type === "variant:update" || event.type === "variant:changed")
          ? event
          : null;
      const variantId = resolveVariantId(eventForId);
      if (variantId == null) return;

      const variantData = getVariantData(variantId);
      if (!variantData) return;

      currentVariantId = variantId;
      rebuildPlanSelect(variantData);
      syncSellingPlan();
      // Packung tiles rebuild async after variant:update; sync prices again once they exist.
      queueMicrotask(() => updateDisplayedPrices(planSelect?.selectedOptions?.[0] || null));
    };

    /** Packung / VE quantity changed — keep Lieferungspreise in sync with ATC. */
    const onPackungChange = () => {
      updateDisplayedPrices(planSelect?.selectedOptions?.[0] || null);
    };

    const gatherSectionIds = () => {
      const sections = [];
      document.dispatchEvent(new CartGroupedSections(sections));
      document.querySelectorAll("cart-items-component").forEach((el) => {
        if (el instanceof HTMLElement && el.dataset.sectionId) {
          sections.push(el.dataset.sectionId);
        }
      });
      return [...new Set(sections.filter(Boolean))];
    };

    /**
     * True when the same gift variant is already in cart as a Spar-Abo incentive
     * (one gift per Warengruppe, keyed by gift product / variant).
     * @param {{ items?: { variant_id?: number; id?: number; properties?: Record<string, string> }[] }} cart
     * @param {number} giftVariantId
     */
    const cartHasAboGiftVariant = (cart, giftVariantId) => {
      const items = cart?.items;
      if (!Array.isArray(items)) return false;
      return items.some((item) => {
        const props = item?.properties || {};
        const isAboGift = props._free_abo === "true" || props._abo_gift === "true";
        if (!isAboGift) return false;
        const lineVariantId = Number(item.variant_id ?? item.id);
        return lineVariantId === Number(giftVariantId);
      });
    };

    /**
     * Adds the configured gratis product after a successful Spar-Abo add-to-cart.
     * Plain cart line (same pattern as cart-drawer free gift) — no parent nesting,
     * which Shopify rejects for non-bundle gifts.
     * Skips add when the same gift variant is already in cart as an abo gift.
     * @param {{ variantId?: string | null }} detail
     */
    const addAboGift = async ({ variantId }) => {
      const giftVariantId = Number(this.dataset.giftVariantId);
      if (!giftVariantId || this.#addingGift) return;
      // Sold-out gift stays visible in the abo UI but cannot be added
      if (this.dataset.giftAvailable === "false") return;

      const mode = this.querySelector("[data-we-site-abonnement-mode]:checked")?.value;
      if (mode !== "abonnement") return;

      this.#addingGift = true;
      try {
        let existingCart = null;
        try {
          existingCart = await fetch(FoxTheme.routes.cart).then((r) => r.json());
        } catch (cartError) {
          console.warn("[we-site-abonnement] cart check skipped:", cartError);
        }
        if (existingCart && cartHasAboGiftVariant(existingCart, giftVariantId)) return;

        const sectionIds = gatherSectionIds();
        const body = {
          items: [
            {
              id: giftVariantId,
              quantity: 1,
              properties: {
                _free_abo: "true",
                _abo_gift: "true",
                _abo_gift_variant_id: String(giftVariantId),
                _abo_parent_product_id: String(productId || ""),
                _abo_parent_variant_id: String(variantId || ""),
              },
            },
          ],
          sections: sectionIds.join(","),
          sections_url: window.location.pathname,
        };

        const res = await fetch(FoxTheme.routes.cart_add_url, fetchConfig("json", { body: JSON.stringify(body) }));
        const parsed = await res.json();

        if (parsed?.status && parsed?.message && !parsed?.items && !parsed?.sections) {
          console.warn("[we-site-abonnement] gift add failed:", parsed.description || parsed.message);
          return;
        }

        // Refresh cart UI with absolute item count after gift add
        let cart = parsed;
        if (!parsed?.item_count && !parsed?.items) {
          cart = await fetch(FoxTheme.routes.cart).then((r) => r.json());
        }

        this.dispatchEvent(
          new CartUpdateEvent(cart, this.id, {
            source: "we-site-abonnement-gift",
            itemCount: cart.item_count || 0,
            productId: String(productId || ""),
            sections: parsed?.sections,
          })
        );
      } catch (error) {
        console.error("[we-site-abonnement] gift add error:", error);
      } finally {
        this.#addingGift = false;
      }
    };

    /**
     * @param {Event} event
     */
    const onCartUpdate = (event) => {
      if (this.#addingGift) return;
      const detail = /** @type {CustomEvent} */ (event).detail;
      const data = detail?.data;
      // CartAddEvent (successful ATC) sets isIncremental; CartUpdateEvent does not
      if (!data?.isIncremental) return;
      if (data.source !== "product-form-component") return;
      if (String(data.productId) !== String(productId)) return;

      addAboGift({ variantId: data.variantId });
    };

    modeInputs.forEach((input) => {
      input.addEventListener(
        "change",
        () => {
          setModeUI(input.value);
          if (input.value === "abonnement" && planSelect && !planSelect.disabled && !planSelect.value) {
            planSelect.selectedIndex = 0;
          }
          if (input.value === "abonnement") updatePricesFromOption(planSelect?.selectedOptions?.[0]);
          syncSellingPlan();
        },
        { signal }
      );
    });

    planSelect?.addEventListener(
      "change",
      () => {
        updatePricesFromOption(planSelect.selectedOptions[0]);
        syncSellingPlan();
      },
      { signal }
    );

    document.addEventListener("variant:selected", onVariantChange, { signal });
    document.addEventListener("variant:update", onVariantChange, { signal });
    document.addEventListener("variant:changed", onVariantChange, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, onCartUpdate, { signal });

    const scope = getScope();
    scope.addEventListener(
      "change",
      (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) return;
        if (target.closest("[data-we-site-packung]")) {
          onPackungChange();
          return;
        }
        // Menge / VE pack-count next to ATC
        if (
          target.matches?.(
            '[data-ve-pack-count], select[name="quantity"], input[name="quantity"], input[ref="quantityInput"]'
          ) ||
          target.closest?.("quantity-selector-component")
        ) {
          onPackungChange();
        }
      },
      { signal }
    );
    scope.addEventListener(
      "input",
      (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) return;
        if (
          target.matches?.(
            '[data-ve-pack-count], select[name="quantity"], input[name="quantity"], input[ref="quantityInput"]'
          )
        ) {
          onPackungChange();
        }
      },
      { signal }
    );
    scope.addEventListener("ve-quantity:sync", onPackungChange, { signal });
    document.addEventListener("ve-quantity:sync", onPackungChange, { signal });

    setModeUI("onetime");
    onVariantChange();
    moveBuyerConsent();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    delete this.dataset.bound;
  }
}

if (!customElements.get("we-site-abonnement")) {
  customElements.define("we-site-abonnement", WeSiteAbonnement);
}
